<template>
  <div class="ca-footer__menu">
    <div class="ca-footer__menu-grid">
      <div
        v-for="(column, colIdx) in columns"
        :key="colIdx"
        class="ca-footer__menu-column"
      >
        <button
          class="ca-footer__menu-toggle"
          :class="{
            'ca-footer__menu-toggle--open': openColumns.has(colIdx),
          }"
          :aria-controls="`footer-menu-${colIdx}`"
          :aria-expanded="openColumns.has(colIdx)"
          @click="toggleColumn(colIdx)"
        >
          <span class="ca-footer__menu-title paragraph-bold">{{
            column.title
          }}</span>
          <IconChevronDown class="ca-footer__menu-chevron" />
        </button>
        <ul
          :id="`footer-menu-${colIdx}`"
          class="ca-footer__menu-list"
          :class="{ 'ca-footer__menu-list--open': openColumns.has(colIdx) }"
        >
          <div class="ca-footer__menu-list-inner">
            <span class="ca-footer__menu-title-desktop paragraph-bold">{{
              column.title
            }}</span>

            <li v-for="(link, linkIdx) in column.links" :key="linkIdx">
              <LinkButton
                class="caption-medium"
                type="grey"
                :label="link.label"
                @click="handleClick(link)"
              />

              <CaStatusTag v-if="link.tag" :label="link.tag" />
            </li>
          </div>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive } from "vue";
import { LinkButton } from "ca-online-design-system";
import CaStatusTag from "../../base/tags/CaStatusTag.vue";
import IconChevronDown from "@assets/icon-chevron-down.svg";

const handleClick = (link) => {
  if (link?.url) {
    window.location.href = link.url;
  }
};

defineProps({
  columns: {
    type: Array,
    required: true,
  },
});

const openColumns = reactive(new Set());

function toggleColumn(idx) {
  if (openColumns.has(idx)) {
    openColumns.delete(idx);
  } else {
    openColumns.add(idx);
  }
}
</script>

<style scoped lang="scss">
.ca-footer__menu-list li + li {
  margin-top: 0px !important;
}

//this is an override for the specific case of footer links
.ca-footer__menu-link {
  color: var(--ca-color-text-light);
}
.ca-footer__menu-link:hover {
  opacity: 1;
  color: var(--ca-white);
}
.ca-footer__menu-link:active {
  color: var(--ca-color-text-body-variant);
}

// O <button> nativo do LinkButton tem text-align:center por defeito,
// o que centra o texto quando o label quebra em colunas estreitas.
.ca-footer__menu-list :deep(.link-button) {
  text-align: left;
}

.ca-footer__menu {
  padding: 0;
  @media (min-width: 1025px) {
    padding: var(--size-7) 0 var(--size-6);
  }
}

.ca-footer__menu-grid {
  display: flex;
  flex-direction: column;
  gap: var(--size-4);

  @media (min-width: 1025px) {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--size-5);
  }
}

// Accordion toggle button — visible on mobile only
.ca-footer__menu-toggle {
  padding: 0px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--ca-color-text-inverted);

  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
    outline-offset: 1px;
  }

  @media (min-width: 1025px) {
    display: none;
  }
}

.ca-footer__menu-chevron {
  transition: transform 400ms ease-out;
  flex-shrink: 0;

  .ca-footer__menu-toggle--open & {
    padding: 0px;
    transform: rotate(180deg);
  }
}

.ca-footer__menu-title {
  margin: 0;
  color: var(--ca-color-text-inverted);

  @media (min-width: 1025px) {
    display: block;
    font-size: var(--font-size-sm);
    margin: 0 0 var(--size-4);
  }
}

.ca-footer__menu-title-desktop {
  display: none;
  margin: 0;
  color: var(--ca-color-text-inverted);

  @media (min-width: 1025px) {
    display: block;
    margin: 0 0 var(--size-4);
  }
}

.ca-footer__menu-list {
  list-style: none;
  margin: 0;
  padding: 0;

  // Mobile: animated accordion
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 400ms ease-out;
  padding-bottom: 0;

  &--open {
    grid-template-rows: 1fr;
    padding-bottom: var(--size-4);
  }

  // Desktop: always visible
  @media (min-width: 1025px) {
    grid-template-rows: 1fr;
    padding-bottom: 0;
  }

  li {
    display: flex;
    align-items: center;
    gap: var(--size-3);
  }

  // li + li {
  //   margin-top: var(--size-3);
  // }
}

.ca-footer__menu-list-inner {
  overflow: hidden;
}
</style>
